'use client';

import { useEffect, useRef } from 'react';
import { sendGTMEvent } from '../sendGTMEvent';
import { getSelectItemData } from '../itemSelectionUtils';
import { productListItemToGAItem } from '../productMappers/productListItemToGAItem';
import { useGTMQueue } from '../useGTMQueue';
import { getBasketFinancials } from '../utils/basketUtils';

/**
 * Tracks the begin_checkout event when users initiate the checkout process.
 * 
 * This component should be rendered on the checkout page to track when users
 * begin the checkout process. It sends a GA4 begin_checkout event with the
 * current basket contents including item details, total value, and currency.
 * 
 * The event is queued and fired when basket data is available.
 * 
 * @returns null - This is a tracking component that doesn't render anything
 */
export const BeginCheckoutTracker = () => {
  const hasAddedToQueue = useRef(false);
  const { addToQueue } = useGTMQueue();

  useEffect(() => {
    const shouldNotTrack = hasAddedToQueue.current;

    if (shouldNotTrack) {
      return;
    }

    addToQueue((injectedData) => {
      const { basket } = injectedData;
      
      // Extract basket financial data
      const { value, currency } = getBasketFinancials(basket);
      
      // Map basket items to GA4 format
      const items = basket?.lines?.map((line) => {
        const itemSelectionData = getSelectItemData(line.productId, line.productId);

        return productListItemToGAItem(line, {
          listId: itemSelectionData?.item_list_id,
          listName: itemSelectionData?.item_list_name,
          index: itemSelectionData?.index,
        });
      }) || [];

      sendGTMEvent({
        event: 'begin_checkout',
        ecommerce: {
          value,
          currency,
          items,
        },
      });
    });
    hasAddedToQueue.current = true;
  }, []);

  return null;
};
